import React, { useState } from 'react';
import { Box } from '@mui/material';
import { selectCurrentUserInfo, updateLayout } from '../../store/slices';
import { useAppDispatch, useAppSelector } from '../../store/store.hook';
import { convertItemsToTableData } from '../../utils';
import { EnhancedTable, SubmitButton } from '../../components';
import {
  useAddItemsToCartMutation,
  useDeleteItemsFromCartMutation,
  useGetItemsInCartQuery,
} from '../../store/apis/decal-order.api';
import { zonesTableTabsTableColumns } from '../../configs';
import { IDecalOrderItem, IDecalOrderItemWithActions } from '../../models/cart.model';
import { IZone } from '../../models';
import { DecalApproveDialog } from '../../dialogs/decal';
import { useZones } from '../zones/zones.hook';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../consts';

export const ReviewCartPage = () => {
  const dispatch = useAppDispatch();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const navigate = useNavigate();
  const [items, setItems] = React.useState<IDecalOrderItem[]>([]);
  const columnKeys = ['decal', 'quantity', 'code', 'name', 'building', 'decalApproved', 'decalApprovedAt', 'actions'];
  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));
  const [decalApproveDialog, setDecalApproveDialog] = useState<{ visible: boolean; zone?: IZone }>({ visible: false });
  const [, { all: zones }] = useZones({});

  const {
    data: order,
    isFetching: fetchingItemsInCart,
    isLoading: loadingItemsInCart,
    isSuccess: loadingItemsInCartSuccess,
    refetch: refreshItemsInCart,
  } = useGetItemsInCartQuery({ orgSlug });

  const [deleteItemFromCart, { isLoading: deleteFromCartLoading, isSuccess: deleteFromCartSuccess }] =
    useDeleteItemsFromCartMutation();

  const [updateItemsInCart] = useAddItemsToCartMutation();

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Review Decal Order' }));
  }, []);

  React.useEffect(() => {
    if (!fetchingItemsInCart && loadingItemsInCartSuccess) {
      setItems(order.items);
    }
  }, [fetchingItemsInCart, loadingItemsInCartSuccess]);

  React.useEffect(() => {
    if (!deleteFromCartLoading && deleteFromCartSuccess) {
      refreshItemsInCart();
    }
  }, [deleteFromCartLoading, deleteFromCartSuccess]);

  const convertOrderItemsToTableData = (orderItems: IDecalOrderItem[]): IDecalOrderItemWithActions[] => {
    return orderItems.map((item) => ({
      ...item,
      reviewDecal: (item) => {
        if (zones) {
          const zone = zones.find((z) => z.id === item.id);
          if (zone) {
            setDecalApproveDialog({
              visible: true,
              zone,
            });
          }
        }
      },
      removeFromCart: (item) => removeItemFromCart(item),
      updateQuantity: (itemId, quantity) => {
        setItems(
          items.map((i) => {
            if (i.id === itemId) {
              return { ...i, quantity };
            }

            return i;
          }),
        );
      },
    }));
  };

  const removeItemFromCart = (item: IDecalOrderItem) => {
    deleteItemFromCart({ orgSlug, zoneId: item.id });
  };

  const handleCheckout = () => {
    // save any changes to quantity
    const zones = items.map((orderItem: IDecalOrderItem) => ({
      zone_id: orderItem.id,
      quantity: orderItem.quantity,
    }));

    updateItemsInCart({ orgSlug, body: { zones } });
    navigate(`/${orgSlug}/${ROUTES.CART}`);
  };

  const handleNavigateToOrderDecalPage = () => {
    navigate(`/${orgSlug}/${ROUTES.ORDER_DECALS}`);
  };

  return (
    <Box padding={3}>
      <EnhancedTable
        config={{ toolbar: false, rowCheckable: false }}
        loading={loadingItemsInCart}
        loadingLabel={'Loading zones'}
        noDataLabel={'No zones'}
        data={convertOrderItemsToTableData(items)}
        convertFunctionForDataToTableData={convertItemsToTableData}
        headColumns={columns}
        columnKeys={columnKeys}
        defaultSortColumnName="floorInfo"
        defaultSortDirection="asc"
        styles={{ tableStyle: { minWidth: 0 } }}
        initialRowsPerPage={10}
        visiblePagination
        rowActions={{}}
      />

      <DecalApproveDialog
        {...decalApproveDialog}
        hideDots
        setVisible={() => setDecalApproveDialog({ visible: false })}
        next={() => {
          refreshItemsInCart();
        }}
      />

      {items.length === 0 ? (
        <SubmitButton label="Add zones to cart" sx={{ mt: 2 }} onClick={handleNavigateToOrderDecalPage} />
      ) : (
        <SubmitButton label="Checkout" sx={{ mt: 2 }} onClick={handleCheckout} disabled={items.length === 0} />
      )}
    </Box>
  );
};
